import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function injectSchema(schema: Record<string, unknown>, id: string) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(schema, null, 2);
  document.head.appendChild(script);

  return () => {
    const el = document.getElementById(id);
    if (el) el.remove();
  };
}

export function HomeSchema() {
  const location = useLocation();

  useEffect(() => {
    const baseUrl = window.location.origin;

    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '52662鼎盛平台',
      url: baseUrl,
      description: '52662鼎盛平台官方版是经过官方认证的正规平台，提供安全稳定的服务体验。',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-XXX-XXXX',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '00:00',
          closes: '23:59',
        },
      },
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '52662鼎盛平台官方版',
      url: baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };

    const webpageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '52662鼎盛平台官方版 - 官方正版下载与注册',
      description: '52662鼎盛平台官方版是经过官方认证的正规平台，提供安全稳定的服务体验。立即访问鼎盛平台官网，享受官方正版保障、极速通道与专属会员权益，注册即享多重福利。',
      url: `${baseUrl}${location.pathname}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '52662鼎盛平台',
        url: baseUrl,
      },
    };

    injectSchema(orgSchema, 'schema-org');
    injectSchema(websiteSchema, 'schema-website');
    injectSchema(webpageSchema, 'schema-webpage');
  }, [location.pathname]);

  return null;
}

export function AboutSchema() {
  useEffect(() => {
    const baseUrl = window.location.origin;

    const aboutPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 52662鼎盛平台',
      description: '了解52662鼎盛平台的品牌故事、发展历程、核心优势与联系方式。官方认证正规平台，值得信赖。',
      url: `${baseUrl}/about`,
      about: {
        '@type': 'Organization',
        name: '52662鼎盛平台',
        foundingDate: '2018',
        description: '52662鼎盛平台成立于2018年，累计服务500万+用户。',
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${baseUrl}/about` },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '52662鼎盛平台是否正规可靠？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '52662鼎盛平台是经过国家相关部门认证备案的正规平台，拥有完善的运营资质和安全保障体系。平台运营多年，积累了数百万用户的信任。',
          },
        },
        {
          '@type': 'Question',
          name: '如何注册鼎盛平台账号？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '注册流程非常简单：点击页面上的"立即注册"按钮，填写基本信息，设置登录密码，完成验证即可。整个过程只需1-2分钟。',
          },
        },
        {
          '@type': 'Question',
          name: '鼎盛平台支持哪些设备？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '鼎盛平台支持PC端（Windows/Mac）、移动端（iOS/Android）以及平板设备，所有数据实时云端同步。',
          },
        },
      ],
    };

    injectSchema(aboutPageSchema, 'schema-about-page');
    injectSchema(breadcrumbSchema, 'schema-breadcrumb-about');
    injectSchema(faqSchema, 'schema-faq');
  }, []);

  return null;
}

export function CategorySchema() {
  useEffect(() => {
    const baseUrl = window.location.origin;

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${baseUrl}/category` },
      ],
    };

    injectSchema(breadcrumbSchema, 'schema-breadcrumb-category');
  }, []);

  return null;
}

export function ArticleSchema({ article }: { article: { title: string; summary: string; date: string; id: string } }) {
  useEffect(() => {
    const baseUrl = window.location.origin;

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      datePublished: article.date,
      url: `${baseUrl}/article/${article.id}`,
      author: {
        '@type': 'Organization',
        name: '52662鼎盛平台',
      },
      publisher: {
        '@type': 'Organization',
        name: '52662鼎盛平台',
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${baseUrl}/category` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `${baseUrl}/article/${article.id}` },
      ],
    };

    injectSchema(articleSchema, 'schema-article');
    injectSchema(breadcrumbSchema, 'schema-breadcrumb-article');
  }, [article]);

  return null;
}